/**
 * Predictive search overlay.
 *
 * Results come from Shopify's native Predictive Search API via the Section
 * Rendering API: GET /search/suggest?...&section_id=predictive-search,
 * rendered server-side by sections/predictive-search.liquid (a static
 * section — see that file for why). This keeps price/vendor/sale-price
 * formatting logic in Liquid (snippets/product-price.liquid) instead of
 * reimplementing it in JS. Search V1 requests both products and
 * collections (resources[type]=product,collection), each with its own
 * limit (resources[limit_scope]=each) so up to 2 collections never eat
 * into the product limit.
 *
 * "Recently viewed" is the one client-rendered path: it reads product
 * handles recorded to localStorage (by snippets/predictive-search.liquid,
 * on product page views) and resolves each via Shopify's native
 * /products/{handle}.js AJAX endpoint.
 *
 * Uses the native <dialog> element for focus trap, Escape-to-close and
 * backdrop-click-to-close — same pattern as mobile-menu-drawer (assets/header.js).
 *
 * Search V1 also turns the input into a proper ARIA combobox: role,
 * aria-expanded, aria-controls (already static in the markup) and
 * aria-activedescendant track a "virtual focus" that moves with
 * ArrowUp/ArrowDown over whichever option list is currently visible
 * (recently-viewed or live results — never both), Enter navigates to the
 * active option (or falls through to the form's normal submit when
 * nothing is active), and native input behavior (typing, native clear via
 * our own button, Escape closing the dialog) is untouched throughout.
 */

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.input = this.querySelector('[data-predictive-search-input]');
        this.resultsEl = this.querySelector('[data-predictive-search-results]');
        this.recentEl = this.querySelector('[data-predictive-search-recent]');
        this.recentList = this.querySelector('[data-predictive-search-recent-list]');
        this.hintEl = this.querySelector('[data-predictive-search-hint]');
        this.footerEl = this.querySelector('[data-predictive-search-footer]');
        this.viewAllButton = this.querySelector('.predictive-search__view-all');
        this.clearRecentButton = this.querySelector('[data-predictive-search-clear-recent]');

        this.recentKey = 'celatta:recently-viewed';
        this.minQueryLength = 2;
        this.debounceMs = 250;
        this.debounceTimer = null;
        this.abortController = null;
        this.recentRenderedFor = null;
        this.activeOptionIndex = -1;

        this.triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`);
        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(trigger);
          });
        });

        this.querySelectorAll('[data-predictive-search-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        this.dialog.addEventListener('close', () => {
          this.input.setAttribute('aria-expanded', 'false');
          this.lastTrigger?.focus();
        });

        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('keydown', this.onInputKeydown.bind(this));
        this.clearRecentButton?.addEventListener('click', () => this.clearRecentlyViewed());
      }

      open(trigger) {
        document.querySelector('mega-menu')?.close?.();
        this.lastTrigger = trigger || document.activeElement;
        this.dialog.showModal();
        this.input.setAttribute('aria-expanded', 'true');
        this.renderRecentlyViewed();
        requestAnimationFrame(() => this.input.focus());
      }

      close() {
        this.dialog.close();
      }

      // The only options ever live in the DOM at once are whichever group is
      // currently visible (recently-viewed OR live results — onInput always
      // hides the other), so this always reflects exactly one navigable list.
      getOptions() {
        return Array.from(this.querySelectorAll('[role="listbox"]:not([hidden]) [role="option"]')).filter(
          (option) => !option.closest('[hidden]')
        );
      }

      setActiveOption(index) {
        const options = this.getOptions();
        this.clearActiveOption();

        if (options.length === 0) {
          this.activeOptionIndex = -1;
          this.input.setAttribute('aria-activedescendant', '');
          return;
        }

        const normalized = ((index % options.length) + options.length) % options.length;
        this.activeOptionIndex = normalized;
        const option = options[normalized];
        option.querySelector('[data-predictive-search-option]')?.setAttribute('data-active', 'true');
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }

      clearActiveOption() {
        this.querySelectorAll('[data-predictive-search-option][data-active]').forEach((el) => el.removeAttribute('data-active'));
      }

      resetActiveOption() {
        this.activeOptionIndex = -1;
        this.clearActiveOption();
        this.input.setAttribute('aria-activedescendant', '');
      }

      onInputKeydown(event) {
        if (event.key === 'ArrowDown') {
          const options = this.getOptions();
          if (options.length === 0) return;
          event.preventDefault();
          this.setActiveOption(this.activeOptionIndex + 1);
        } else if (event.key === 'ArrowUp') {
          const options = this.getOptions();
          if (options.length === 0) return;
          event.preventDefault();
          this.setActiveOption(this.activeOptionIndex - 1);
        } else if (event.key === 'Enter') {
          const options = this.getOptions();
          const active = options[this.activeOptionIndex];
          const link = active?.querySelector('[data-predictive-search-option]');
          if (link) {
            event.preventDefault();
            window.location.href = link.href;
          }
          // No active option: let the form submit natively to /search.
        }
        // Escape is left untouched — the native <dialog> already closes on
        // Escape regardless of which descendant currently holds focus.
      }

      onInput(event) {
        const query = event.target.value.trim();
        clearTimeout(this.debounceTimer);
        this.resetActiveOption();

        if (query.length === 0) {
          this.abortController?.abort();
          this.resultsEl.innerHTML = '';
          this.resultsEl.removeAttribute('data-loading');
          this.footerEl.hidden = true;
          this.hintEl.hidden = false;
          this.renderRecentlyViewed();
          return;
        }

        this.recentEl.hidden = true;

        if (query.length < this.minQueryLength) {
          this.resultsEl.innerHTML = '';
          this.footerEl.hidden = true;
          this.hintEl.hidden = false;
          return;
        }

        this.hintEl.hidden = true;
        this.debounceTimer = setTimeout(() => this.search(query), this.debounceMs);
      }

      async search(query) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.resultsEl.setAttribute('data-loading', 'true');

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url =
          `${root}search/suggest` +
          `?q=${encodeURIComponent(query)}` +
          '&resources[type]=product,collection' +
          '&resources[limit]=8' +
          '&resources[limit_scope]=each' +
          '&resources[options][unavailable_products]=last' +
          '&section_id=predictive-search';

        try {
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fragment = doc.querySelector('#shopify-section-predictive-search');
          this.resultsEl.innerHTML = fragment ? fragment.innerHTML : '';
          this.resetActiveOption();

          // Bug fixed here: the footer used to show "ver todos los
          // resultados" even on zero matches. It's only ever shown now when
          // the fragment actually contains at least one option, and its
          // label carries the real query so it always reads "...para 'X'".
          const hasResults = Boolean(fragment && fragment.querySelector('[role="option"]'));
          this.footerEl.hidden = !hasResults;
          if (hasResults && this.viewAllButton) {
            const label = this.dataset.viewAllLabel || '';
            this.viewAllButton.textContent = `${label} "${query}"`.trim();
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          const message = this.dataset.errorMessage || 'Something went wrong. Try again.';
          this.resultsEl.innerHTML = `<p class="predictive-search__error">${this.escapeHtml(message)}</p>`;
          this.footerEl.hidden = true;
        } finally {
          this.resultsEl.removeAttribute('data-loading');
        }
      }

      async renderRecentlyViewed() {
        if (this.input.value.trim().length > 0) {
          this.recentEl.hidden = true;
          return;
        }

        let handles = [];
        try {
          handles = JSON.parse(localStorage.getItem(this.recentKey) || '[]');
        } catch (error) {
          handles = [];
        }

        if (!Array.isArray(handles) || handles.length === 0) {
          this.recentEl.hidden = true;
          return;
        }

        const cacheKey = handles.join(',');
        if (this.recentRenderedFor === cacheKey) {
          this.recentEl.hidden = false;
          return;
        }

        try {
          const products = await Promise.all(
            handles.slice(0, 5).map((handle) =>
              fetch(`/products/${handle}.js`)
                .then((response) => (response.ok ? response.json() : null))
                .catch(() => null)
            )
          );
          const valid = products.filter(Boolean);

          if (valid.length === 0) {
            this.recentEl.hidden = true;
            return;
          }

          this.recentList.innerHTML = valid.map((product, index) => this.renderRecentItem(product, index)).join('');
          this.recentRenderedFor = cacheKey;
          this.recentEl.hidden = false;
          this.resetActiveOption();
        } catch (error) {
          this.recentEl.hidden = true;
        }
      }

      renderRecentItem(product, index) {
        const image = product.featured_image || (product.images && product.images[0]) || null;
        const onSale = product.compare_at_price > product.price;
        const price = this.formatMoney(product.price);
        const compareAt = onSale ? this.formatMoney(product.compare_at_price) : null;

        return `
          <li role="option" id="predictive-search-recent-option-${index}">
            <a href="${product.url}" class="predictive-search-result" data-predictive-search-option>
              <span class="predictive-search-result__media">
                ${
                  image
                    ? `<img src="${this.resizeImage(image, 300)}" alt="" class="predictive-search-result__image" loading="lazy" width="300" height="300">`
                    : ''
                }
              </span>
              <span class="predictive-search-result__info">
                ${product.vendor ? `<span class="predictive-search-result__vendor text-micro">${this.escapeHtml(product.vendor)}</span>` : ''}
                <span class="predictive-search-result__title">${this.escapeHtml(product.title)}</span>
                <span class="price${onSale ? ' price--on-sale' : ''}">
                  <span class="price__sale">${price}</span>
                  ${compareAt ? `<span class="price__compare-at">${compareAt}</span>` : ''}
                </span>
              </span>
            </a>
          </li>
        `;
      }

      resizeImage(url, width) {
        return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
      }

      formatMoney(cents) {
        // Built manually (symbol + toLocaleString) rather than through
        // Intl's currency style: that route depends on
        // document.documentElement.lang, which here is the generic "es" —
        // some engines format MXN under that without grouping or a symbol
        // ("1999 MXN" instead of "$ 1,999"). This also matches the shop's
        // own money_without_trailing_zeros output ("$ 1,999") exactly,
        // which the server-rendered predictive search results use.
        const amount = Math.round(cents) / 100;
        return `$ ${amount.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value;
        return div.innerHTML;
      }

      clearRecentlyViewed() {
        try {
          localStorage.removeItem(this.recentKey);
        } catch (error) {
          /* no-op */
        }
        this.recentRenderedFor = null;
        this.recentList.innerHTML = '';
        this.recentEl.hidden = true;
        // The clicked button lives inside .recentEl, which just got
        // hidden — without moving focus, it falls out of the dialog's
        // trap entirely (back to <body>). Send it somewhere still visible.
        this.input.focus();
      }
    }
  );
}
